import React, { ReactNode, useCallback, useEffect, useState } from 'react'

import LoadingSection from 'components_new/molecules/LoadingSection'

import GenericWidgetFullSizeWrapper, {
  GenericWidgetFullSizeWrapperProps
} from './GenericWidgetFullSizeWrapper'
import GenericWidgetBase from './GenericWidgetBase'
import GenericWidgetBody from './GenericWidgetBody'
import GenericWidgetFooter from './GenericWidgetFooter'
import GenericWidgetHeader from './GenericWidgetHeader'
import GenericWidgetMenu from './GenericWidgetMenu'

import { OptionProps } from 'components_new/molecules/OptionsMenu'
import { InnerWidgetPatchBody } from 'types/GlobalWidget'

interface GenericWidgetProps {
  children: ReactNode
  disableBorder?: boolean
  disableFullsize?: boolean
  disablePadding?: boolean
  disableSubtitle?: boolean
  disableTitle?: boolean
  editMode: boolean
  footer?: ReactNode
  fullSize?: {
    active: boolean
    onClose?: () => void
    offset?: GenericWidgetFullSizeWrapperProps['offset']
  }
  loading?: boolean
  onSettings?: () => void
  options?: OptionProps[]
  scaleFactor?: number
  subtitle?: string | null
  title?: string | null
  tooltip?: string | null
  updateWidget?: (body: InnerWidgetPatchBody) => void
}

export const STANDARD_WIDGET_SCALE_FACTOR = 0.8

const GenericWidget = (props: GenericWidgetProps) => {
  const {
    children,
    disableBorder,
    disableFullsize,
    disablePadding,
    disableSubtitle,
    disableTitle,
    editMode,
    footer,
    fullSize: controlledFullSize,
    loading,
    onSettings,
    options,
    scaleFactor,
    subtitle,
    title,
    tooltip,
    updateWidget
  } = props

  // full size
  const [fullSize, setFullSize] = useState<boolean>(false)

  useEffect(() => {
    setFullSize(Boolean(controlledFullSize?.active))
  }, [controlledFullSize?.active])

  const handleSetFullSize = useCallback(
    (value: React.SetStateAction<boolean>) => {
      const newValue = typeof value === 'function' ? value(fullSize) : value

      setFullSize(newValue)

      if (!newValue) {
        controlledFullSize?.onClose?.()
      }
    },
    [fullSize, controlledFullSize?.onClose]
  )

  // scale
  const activeScaleFactor = scaleFactor ?? STANDARD_WIDGET_SCALE_FACTOR

  return (
    <>
      <GenericWidgetBase
        disableBorder={disableBorder}
        disablePadding={disablePadding}
        editMode={editMode}
        hasHeader={!disableSubtitle || !disableTitle}
        scaleFactor={activeScaleFactor}
      >
        <GenericWidgetFullSizeWrapper
          dashboardFilter={[]} // $TODO
          fullSize={fullSize}
          offset={
            controlledFullSize?.active
              ? {
                  right: controlledFullSize?.offset?.right
                }
              : undefined
          }
          setFullSize={handleSetFullSize}
        >
          <GenericWidgetMenu
            disableFullsize={disableFullsize}
            editMode={editMode}
            fullSize={fullSize}
            onFullSize={() => handleSetFullSize(!fullSize)}
            onSettings={onSettings}
            options={options}
            scaleFactor={activeScaleFactor}
          />
          <GenericWidgetHeader
            editMode={editMode}
            disableSubtitle={disableSubtitle}
            disableTitle={disableTitle}
            onChangeSubtitle={(value) => {
              if (updateWidget) updateWidget({ subtitle: value || '' })
            }}
            onChangeTitle={(value) => {
              if (updateWidget) updateWidget({ title: value || '' })
            }}
            scaleFactor={activeScaleFactor}
            subtitle={subtitle}
            title={title}
            tooltip={tooltip}
          />
          {loading ? (
            <LoadingSection />
          ) : (
            <>
              <GenericWidgetBody>{children}</GenericWidgetBody>
              {footer ? (
                <GenericWidgetFooter sx={{ mt: 1 * activeScaleFactor }}>
                  {footer}
                </GenericWidgetFooter>
              ) : null}
            </>
          )}
        </GenericWidgetFullSizeWrapper>
      </GenericWidgetBase>
    </>
  )
}

export default GenericWidget
